import React, { useEffect, useState } from 'react';
import './ServiceFormPage.css';
import {
  buildInitialValues,
  createServicePaymentOrder,
  isFieldVisible,
  submitServiceRequest,
  validateServiceField,
  validateServiceForm,
  verifyServicePayment,
} from '../../utils/formSubmission';
const {
  formatPaymentAmount,
  getServicePaymentConfig,
  isServicePaymentEnabled,
} = require('../../shared/servicePayments');

const CONTACT_CHANNELS = [
  { label: 'Email', value: 'tharenterprisespune@gmail.com' },
  { label: 'Phone', value: '+91 96642 23533' },
  {
    label: 'Office',
    value: 'Office No 2-3, Shreyas Corner, Opp Union Bank, Nanded Phata, Pune-68',
  },
];

const getFieldClassName = (field) =>
  `service-form__field${field.width === 'full' || field.type === 'textarea' || field.type === 'file' ? ' service-form__field--full' : ''}`;

const getFieldValue = (field, values) => values[field.name] || (field.type === 'file' ? [] : '');

const RAZORPAY_SCRIPT_SELECTOR = 'script[data-razorpay-checkout="true"]';

const loadRazorpayCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }

    const existingScript = document.querySelector(RAZORPAY_SCRIPT_SELECTOR);

    if (existingScript) {
      existingScript.addEventListener('load', () => resolve(window.Razorpay), { once: true });
      existingScript.addEventListener('error', () => reject(new Error('Unable to load the payment gateway.')), {
        once: true,
      });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.dataset.razorpayCheckout = 'true';
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error('Unable to load the payment gateway.'));
    document.body.appendChild(script);
  });

const openPaymentCheckout = (Razorpay, order) =>
  new Promise((resolve, reject) => {
    let settled = false;

    const settle = (callback, value) => {
      if (settled) {
        return;
      }

      settled = true;
      callback(value);
    };

    const paymentInstance = new Razorpay({
      key: order.keyId,
      amount: order.amount,
      currency: order.currency,
      name: order.companyName,
      description: order.description,
      order_id: order.orderId,
      prefill: order.prefill,
      handler: (response) => {
        settle(resolve, response);
      },
      modal: {
        ondismiss: () => {
          settle(reject, new Error('Payment was cancelled before completion.'));
        },
      },
      theme: {
        color: '#1b9fcf',
      },
    });

    paymentInstance.on('payment.failed', (event) => {
      const reason =
        event?.error?.description ||
        event?.error?.reason ||
        'Payment failed. Please try again.';

      settle(reject, new Error(reason));
    });

    paymentInstance.open();
  });

const getSubmitButtonLabel = (serviceTitle, submissionStep, paymentEnabled) => {
  if (submissionStep === 'creating-order') {
    return paymentEnabled ? 'Preparing payment...' : 'Submitting request...';
  }

  if (submissionStep === 'processing-payment') {
    return 'Waiting for payment...';
  }

  if (submissionStep === 'verifying-payment') {
    return 'Verifying payment...';
  }

  if (submissionStep === 'submitting-request') {
    return 'Submitting request...';
  }

  return paymentEnabled ? `Pay & Submit ${serviceTitle}` : `Submit ${serviceTitle}`;
};

const transformFieldValue = (field, rawValue) => {
  if (field.transform === 'uppercase') {
    return rawValue.toUpperCase().replace(/\s+/g, '');
  }

  return rawValue;
};

const ServiceRequestForm = ({ service }) => {
  const paymentEnabled = isServicePaymentEnabled();
  const paymentConfig = getServicePaymentConfig(service.id);
  const paymentAmountLabel = formatPaymentAmount(paymentConfig.amountPaise, paymentConfig.currency);
  const [values, setValues] = useState(() => buildInitialValues(service.fields));
  const [errors, setErrors] = useState({});
  const [submissionStep, setSubmissionStep] = useState('idle');
  const [submitted, setSubmitted] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const isSubmitting = submissionStep !== 'idle';

  useEffect(() => {
    setValues(buildInitialValues(service.fields));
    setErrors({});
    setSubmitted(false);
    setSubmissionStep('idle');
    setFormKey((current) => current + 1);
  }, [service]);

  const handleInputChange = (field, event) => {
    const nextValue = transformFieldValue(field, event.target.value);
    const nextValues = {
      ...values,
      [field.name]: nextValue,
    };

    setValues(nextValues);

    setErrors((current) => {
      const nextErrors = {
        ...current,
        [field.name]: validateServiceField(field, nextValue, nextValues),
      };

      service.fields.forEach((serviceField) => {
        if (!isFieldVisible(serviceField, nextValues)) {
          nextErrors[serviceField.name] = undefined;
        }
      });

      return nextErrors;
    });
  };

  const handleFileChange = (field, event) => {
    const nextFiles = Array.from(event.target.files || []);
    const nextValues = {
      ...values,
      [field.name]: nextFiles,
    };

    setValues(nextValues);

    setErrors((current) => ({
      ...current,
      [field.name]: validateServiceField(field, nextFiles, nextValues),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = validateServiceForm(service.fields, values);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitted(false);
    setSubmissionStep('creating-order');

    try {
      let verifiedPayment = null;

      if (paymentEnabled) {
        const order = await createServicePaymentOrder(service, values);
        const Razorpay = await loadRazorpayCheckout();

        setSubmissionStep('processing-payment');
        const paymentResult = await openPaymentCheckout(Razorpay, order);

        setSubmissionStep('verifying-payment');
        const verification = await verifyServicePayment({
          orderId: paymentResult.razorpay_order_id,
          paymentId: paymentResult.razorpay_payment_id,
          signature: paymentResult.razorpay_signature,
        });

        verifiedPayment = verification.payment;
      }

      setSubmissionStep('submitting-request');
      await submitServiceRequest(service, service.fields, values, verifiedPayment);
      setSubmitted(true);
      setValues(buildInitialValues(service.fields));
      setErrors({});
      setFormKey((current) => current + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      alert(error.message || 'Submission failed. Please try again.');
      console.error('Service request failed:', error);
    } finally {
      setSubmissionStep('idle');
    }
  };

  const visibleFields = service.fields.filter((field) => isFieldVisible(field, values));

  return (
    <div className="service-form__card">
      <div className="service-form__header">
        <div>
          <p className="service-form__eyebrow">Request Form</p>
          <h2 className="service-form__title">Submit your details</h2>
        </div>
        <p className="service-form__note">All fields are mandatory. Keep all uploaded documents under 3 MB combined.</p>
      </div>

      {submitted && (
        <div className="service-form__success">
          {paymentEnabled
            ? 'Payment received and request submitted successfully. The Thar Enterprises team has received your details and documents.'
            : 'Request submitted successfully. The Thar Enterprises team has received your details and documents.'}
        </div>
      )}

      <form key={formKey} className="service-form" onSubmit={handleSubmit} noValidate>
        <div className="service-form__grid">
          {visibleFields.map((field) => {
            const fieldValue = getFieldValue(field, values);
            const error = errors[field.name];

            return (
              <div key={field.name} className={getFieldClassName(field)}>
                <label className="service-form__label" htmlFor={field.name}>
                  {field.label}
                </label>

                {field.type === 'textarea' && (
                  <textarea
                    id={field.name}
                    className="service-form__input service-form__textarea"
                    name={field.name}
                    placeholder={field.placeholder}
                    value={fieldValue}
                    onChange={(event) => handleInputChange(field, event)}
                    rows={5}
                    maxLength={field.maxLength}
                    disabled={isSubmitting}
                  />
                )}

                {field.type === 'select' && (
                  <select
                    id={field.name}
                    className="service-form__input"
                    name={field.name}
                    value={fieldValue}
                    onChange={(event) => handleInputChange(field, event)}
                    disabled={isSubmitting}
                  >
                    <option value="">Select an option</option>
                    {field.options.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}

                {field.type === 'file' && (
                  <>
                    <input
                      id={field.name}
                      className="service-form__input service-form__file"
                      type="file"
                      name={field.name}
                      accept={field.accept}
                      multiple={Boolean(field.maxFiles && field.maxFiles > 1)}
                      onChange={(event) => handleFileChange(field, event)}
                      disabled={isSubmitting}
                    />
                    {fieldValue.length > 0 && (
                      <p className="service-form__file-list">
                        {fieldValue.map((file) => file.name).join(', ')}
                      </p>
                    )}
                  </>
                )}

                {!['textarea', 'select', 'file'].includes(field.type) && (
                  <input
                    id={field.name}
                    className="service-form__input"
                    type={field.type}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={fieldValue}
                    onChange={(event) => handleInputChange(field, event)}
                    autoComplete={field.autoComplete}
                    inputMode={field.inputMode}
                    maxLength={field.maxLength}
                    disabled={isSubmitting}
                  />
                )}

                {field.helpText && <p className="service-form__hint">{field.helpText}</p>}
                {error && <p className="service-form__error">{error}</p>}
              </div>
            );
          })}
        </div>

        <div className="service-form__consent">
          <p className="service-form__consent-title">Terms and Consent Notice</p>
          <p className="service-form__consent-copy">
            By submitting this service request form, you confirm that the information and documents
            provided are shared by you voluntarily and may be processed by Thar Enterprises for
            preparing, applying, and following up on this service through the relevant government
            portals and compliance systems.
          </p>
        </div>

        {paymentEnabled && (
          <div className="service-form__payment-note">
            <p className="service-form__payment-title">Payment and Final Submission</p>
            <p className="service-form__payment-copy">
              When you continue, a secure {paymentAmountLabel} payment checkout will open first. We
              will send the final service request to the team only after the payment is successfully
              verified.
            </p>
          </div>
        )}

        <button className="service-form__submit" type="submit" disabled={isSubmitting}>
          {getSubmitButtonLabel(service.title, submissionStep, paymentEnabled)}
        </button>
      </form>
    </div>
  );
};

const ServiceFormPage = ({ service }) => {
  const documentFields = service.fields.filter((field) => field.type === 'file');
  const paymentConfig = getServicePaymentConfig(service.id);
  const priceLabel = formatPaymentAmount(paymentConfig.amountPaise, paymentConfig.currency);

  return (
    <main className="service-page">
      <section className="service-page__hero">
        <div className="container service-page__hero-inner">
          <a className="service-page__back" href="/#services">
            Back to all services
          </a>
          <div className="service-page__hero-badge">
            <service.Icon />
          </div>
          <h1 className="service-page__title">{service.pageTitle}</h1>
          <p className="service-page__subtitle">{service.pageSubtitle}</p>
        </div>
      </section>

      <section className="service-page__body">
        <div className="container service-page__grid">
          <aside className="service-page__sidebar">
            <div className="service-page__card">
              <p className="service-page__card-label">Service Overview</p>
              <h2 className="service-page__card-title">{service.title}</h2>
              <p className="service-page__card-copy">{service.description}</p>
              <div className="service-page__price">
                <span className="service-page__price-label">Service Fee</span>
                <strong className="service-page__price-value">{priceLabel}</strong>
              </div>
              <ul className="service-page__list">
                {service.highlights.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="service-page__card">
              <p className="service-page__card-label">Required Documents</p>
              <ul className="service-page__list">
                {documentFields.map((field) => (
                  <li key={field.name}>{field.label}</li>
                ))}
              </ul>
            </div>

            <div className="service-page__card service-page__card--support">
              <p className="service-page__card-label">Need Help?</p>
              <h2 className="service-page__card-title">Speak with the team</h2>
              <ul className="service-page__contact-list">
                {CONTACT_CHANNELS.map((item) => (
                  <li key={item.label}>
                    <span>{item.label}</span>
                    <strong>{item.value}</strong>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <ServiceRequestForm service={service} />
        </div>
      </section>
    </main>
  );
};

export default ServiceFormPage;
